import { DEFAULT_PHONE_ISO_CODE } from './countries';
import type { ContactFormValues, ContactUsFormValues } from '../types';
import {
  resolvePhoneIsoCode,
  type WebsiteFormPayload,
} from '../utils/websiteForm';

export const CAUSE_OPTIONS = [
  'Vidya',
  'Kaushal',
  'Arti',
  'Swasthya',
  'Other',
] as const;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[+]?[1-9][\d]{0,15}$/;

export function validatePhoneNumber(phone: string): string | undefined {
  if (!phone.trim()) {
    return 'Phone number is required';
  }

  if (!PHONE_REGEX.test(phone.replace(/\s/g, ''))) {
    return 'Please enter a valid phone number';
  }

  return undefined;
}

export function validateEmail(
  email: string,
  emptyMessage = 'Email is required'
): string | undefined {
  if (!email.trim()) {
    return emptyMessage;
  }

  if (!EMAIL_REGEX.test(email)) {
    return 'Please enter a valid email address';
  }

  return undefined;
}

export const contactFormModalInitialValues: ContactFormValues = {
  firstName: '',
  lastName: '',
  phoneIsoCode: DEFAULT_PHONE_ISO_CODE,
  phoneNumber: '',
  emailAddress: '',
  organisation: '',
  location: '',
  causeInterested: '',
  briefMessage: '',
};

export const contactUsFormInitialValues: ContactUsFormValues = {
  cause: '',
  firstName: '',
  lastName: '',
  phoneIsoCode: DEFAULT_PHONE_ISO_CODE,
  phoneNumber: '',
  email: '',
  message: '',
};

export function validateContactFormModal(
  values: ContactFormValues
): Partial<ContactFormValues> {
  const errors: Partial<ContactFormValues> = {};

  if (!values.firstName.trim()) {
    errors.firstName = 'First Name is required';
  }

  if (!values.lastName.trim()) {
    errors.lastName = 'Last Name is required';
  }

  const phoneError = validatePhoneNumber(values.phoneNumber);
  if (phoneError) {
    errors.phoneNumber = phoneError;
  }

  const emailError = validateEmail(values.emailAddress, 'Email is required');
  if (emailError) {
    errors.emailAddress = emailError;
  }

  if (!values.organisation.trim()) {
    errors.organisation = 'Organisation is required';
  }

  if (!values.causeInterested.trim()) {
    errors.causeInterested = 'Cause Interested In is required';
  }

  if (!values.briefMessage.trim()) {
    errors.briefMessage = 'Brief Message is required';
  }

  return errors;
}

export function validateContactUsForm(
  values: ContactUsFormValues
): Partial<ContactUsFormValues> {
  const errors: Partial<ContactUsFormValues> = {};

  if (!values.cause.trim()) {
    errors.cause = 'Please select a cause';
  }

  if (!values.firstName.trim()) {
    errors.firstName = 'First name is required';
  }

  if (!values.lastName.trim()) {
    errors.lastName = 'Last name is required';
  }

  const phoneError = validatePhoneNumber(values.phoneNumber);
  if (phoneError) {
    errors.phoneNumber = phoneError;
  }

  const emailError = validateEmail(values.email);
  if (emailError) {
    errors.email = emailError;
  }

  if (!values.message.trim()) {
    errors.message = 'Message is required';
  }

  return errors;
}

export function buildContactFormModalPayload(
  values: ContactFormValues
): WebsiteFormPayload {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    phoneNumber: values.phoneNumber.trim(),
    phoneIsoCode: resolvePhoneIsoCode(values.phoneIsoCode),
    emailAddress: values.emailAddress.trim(),
    organisation: values.organisation.trim(),
    location: values.location.trim(),
    causeInterestedIn: values.causeInterested,
    briefMessage: values.briefMessage.trim(),
  };
}

export function buildContactUsFormPayload(
  values: ContactUsFormValues
): WebsiteFormPayload {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    phoneNumber: values.phoneNumber.trim(),
    phoneIsoCode: resolvePhoneIsoCode(values.phoneIsoCode),
    emailAddress: values.email.trim(),
    organisation: '',
    location: '',
    causeInterestedIn: values.cause,
    briefMessage: values.message.trim(),
  };
}
